<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { toast } from "@/common/utils/toast";
import store from "@/store";

import {
  evaluationList,
  evaluationShow,
} from "@/common/api/rest/evaluation/evaluation";

import type {
  EvaluationType,
  EvaluationListType,
} from "@/common/types/evaluation";

import { EvaluationTheme } from "@/common/themes";
import {
  evaluationAnalysis,
  evaluationCompany,
  evaluationCrew,
  evaluationEquipment,
  evaluationEmployees,
  evaluationInfos,
  evaluationLaboratory,
  evaluationSamples,
  evaluationReport,
} from "@/components";

const { t } = useI18n();
const route = useRoute();

const searchInput = ref("");

const evaluation = ref<EvaluationType | null>(null);
const evaluations = ref<EvaluationListType[]>([]);

const accountId = computed(() => route.params.id as string);
const evaluationId = computed(() => route.params.evaluation as string);

const tabList = computed<{ [key: string]: any }>(() => ({
  info: {
    key: "info",
    icon: "settings",
    label: t("common.labels.infos"),
    component: evaluationInfos,
    props: {
      actionShow: false,
    },
  },
  report: {
    key: "report",
    icon: "form",
    label: t("common.labels.reports"),
    component: evaluationReport,
    props: {},
  },
  analysis: {
    key: "analysis",
    icon: "list",
    label: t("common.labels.analysis"),
    component: evaluationAnalysis,
    props: {},
  },
  crew: {
    key: "crew",
    icon: "technical",
    label: t("common.labels.crew"),
    component: evaluationCrew,
    props: {},
  },
  equipment: {
    key: "equipment",
    icon: "equipment",
    label: t("common.labels.equipments"),
    component: evaluationEquipment,
    props: {},
  },
  laboratory: {
    key: "laboratory",
    icon: "laboratory",
    label: t("common.labels.laboratories"),
    component: evaluationLaboratory,
    props: {},
  },
  samples: {
    key: "samples",
    icon: "samples",
    label: t("common.labels.samples"),
    component: evaluationSamples,
    props: {},
  },
  company: {
    key: "company",
    icon: "company",
    label: t("common.labels.company"),
    component: evaluationCompany,
    props: {},
  },
  employees: {
    key: "employees",
    icon: "card",
    label: t("common.labels.employees"),
    component: evaluationEmployees,
    props: {},
  },
}));

const menuList = Object.values(tabList.value)?.reduce(
  (acc: any[], item: any) => {
    acc.push({
      key: item.key,
      icon: item.icon,
      label: item.label,
    });
    return acc;
  },
  [],
);

function fetchData() {
  if (!accountId.value || !evaluationId.value) return;

  evaluationShow(accountId.value, evaluationId.value)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        evaluation.value = data;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function fetchEvaluations() {
  if (!accountId.value) return;

  evaluationList(accountId.value, {
    full: true,
    status: "PROGRESS",
    search: searchInput.value,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        evaluations.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function inputSearch(value: string) {
  searchInput.value = value;

  if (!value) fetchEvaluations();
}

function search() {
  fetchEvaluations();
}

function refetch() {
  fetchData();
  fetchEvaluations();
}

onMounted(() => {
  store.commit("SET_LOADING", true);
  refetch();
});
</script>

<template>
  <evaluation-theme
    :is-edit="false"
    :account-id="accountId"
    :evaluation-id="evaluationId"
    :evaluation="evaluation"
    :evaluations="evaluations"
    :tab-list="tabList"
    :menu-list="menuList"
    @update-data="refetch()"
    @input-search="inputSearch"
    @search="search"
  />
</template>

<style lang="scss" scoped>
.evaluation-page {
  display: flex;
}
</style>
